// Uso del ERP: tracker ligero de pantallas y acciones (lo lee dirección en
// /uso-erp para detectar trabajo repetitivo y automatizaciones).
// - Pantallas: RastreadorUso (App.js) llama a registrarPagina en cada cambio
//   de ruta; cada entrada lleva el tiempo pasado en la pantalla anterior.
// - Acciones: un único listener de clic (fase de captura) en botones, enlaces,
//   pestañas, elementos de menú y cualquier [data-uso]. Nunca lee valores de
//   inputs ni texto de formularios: solo data-uso o el texto visible del botón.
// - Cola en memoria; envío cada 15 s, al llegar a 50 eventos y al ocultar o
//   cerrar la pestaña (fetch con keepalive). Máximo 500 en cola.
// - En la vista de simulación o sin sesión no registra nada.
// - Señales de que algo no funciona: errores de JavaScript (error y
//   unhandledrejection), respuestas del API >= 400 (observador del interceptor
//   de api/api.js; sin 401 ni las propias llamadas a /uso/*), ráfagas de 3 o
//   más clics sobre el mismo botón en menos de 2 s y recargas de la pantalla.
// - Mide a todos los empleados y a la lista fija (backend, USUARIOS_MEDIDOS): al
//   iniciar sesión consulta GET /uso/estado una vez (cacheado en
//   sessionStorage por usuario) y, si `activo` es false, ni registra ni envía.
import api, { getAuthToken, setObservadorErroresApi } from '../api/api';

const CLAVE_SESION = 'uso_erp_sesion';
const PREFIJO_ESTADO = 'uso_erp_activo:';
const CLAVE_RECARGA = 'uso_erp_ultima_recarga';
const VENTANA_RAFAGA_MS = 2000;
const MIN_CLICS_RAFAGA = 3;
const RECARGA_SEGUIDA_MS = 60 * 1000;
const SILENCIO_ERROR_MS = 10 * 1000; // el mismo error no se repite antes de 10 s
const RE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const INACTIVIDAD_MS = 30 * 60 * 1000;
const INTERVALO_ENVIO_MS = 15 * 1000;
const UMBRAL_ENVIO = 50;
const MAX_COLA = 500;
const MAX_LOTE = 200;
const MAX_LOTE_KEEPALIVE = 100; // keepalive admite cuerpos de unos 64 KB
const SELECTOR_ACCION = 'button, a, [role="tab"], [role="menuitem"], [data-uso]';
const PARAMS_SENSIBLES = /^(token|access_token|t|jwt|auth|key|password|code)$/i;

let config = null;
let iniciado = false;
let cola = [];
let enviando = false;
let paginaActual = null; // { ruta, inicio, oculta }
let ultimoEvento = 0;
let sesionMemoria = null;
const estadoMemoria = {}; // { [usuarioKey]: true | false }
let consultandoEstado = null; // usuarioKey con la consulta en curso
let usuarioDeLaCola = null; // la cola es de una sola persona: si cambia la sesión, se vacía
let rafaga = null; // { clave, nombre, ruta, primero, ultimo, n, temporizador }
const ultimosErrores = {}; // { [mensaje]: instante }

const ahora = () => Date.now();

const nuevoId = () => {
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  } catch (e) {
    // sin crypto: id pseudoaleatorio
  }
  return `${ahora().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

// Sesión por pestaña (sessionStorage); nueva tras 30 minutos sin eventos
const obtenerSesionId = () => {
  const t = ahora();
  let sesion = null;
  try {
    const texto = window.sessionStorage.getItem(CLAVE_SESION);
    sesion = texto ? JSON.parse(texto) : null;
  } catch (e) {
    sesion = sesionMemoria;
  }
  if (!sesion || !sesion.id || t - (sesion.ultimo || 0) > INACTIVIDAD_MS) {
    sesion = { id: nuevoId(), ultimo: t };
  } else {
    sesion = { id: sesion.id, ultimo: t };
  }
  sesionMemoria = sesion;
  try {
    window.sessionStorage.setItem(CLAVE_SESION, JSON.stringify(sesion));
  } catch (e) {
    // sin sessionStorage (modo privado): se queda en memoria
  }
  return sesion.id;
};

const claveUsuario = () => {
  try {
    const usuario = config && config.getUsuario ? config.getUsuario() : null;
    if (!usuario) return null;
    return String(usuario.empleado_id || usuario.id || usuario.email || '') || null;
  } catch (e) {
    return null;
  }
};

const leerEstado = (clave) => {
  if (clave in estadoMemoria) return estadoMemoria[clave];
  try {
    const valor = window.sessionStorage.getItem(PREFIJO_ESTADO + clave);
    if (valor === '1' || valor === '0') {
      estadoMemoria[clave] = valor === '1';
      return estadoMemoria[clave];
    }
  } catch (e) {
    // sin sessionStorage: solo memoria
  }
  return undefined;
};

// Una consulta por usuario y pestaña; mientras no se sabe, los eventos esperan en la cola
const consultarEstado = (clave) => {
  if (consultandoEstado === clave) return;
  consultandoEstado = clave;
  api.get('/uso/estado')
    .then((res) => {
      const activo = !!(res && res.data && res.data.activo);
      estadoMemoria[clave] = activo;
      try {
        window.sessionStorage.setItem(PREFIJO_ESTADO + clave, activo ? '1' : '0');
      } catch (e) {
        // sin sessionStorage: solo memoria
      }
      if (!activo) cola = [];
    })
    .catch(() => {
      // Sin respuesta: no se mide en esta pestaña (no se insiste)
      estadoMemoria[clave] = false;
      cola = [];
    })
    .finally(() => {
      if (consultandoEstado === clave) consultandoEstado = null;
    });
};

// true: se mide; false: no; undefined: aún no se sabe (consulta en curso)
const estadoActivo = () => {
  const clave = claveUsuario();
  if (!clave) return false;
  const estado = leerEstado(clave);
  if (estado === undefined) consultarEstado(clave);
  return estado;
};

const puedeRegistrar = () => {
  if (!config) return false;
  try {
    if (!getAuthToken()) return false;
    if (!config.getUsuario || !config.getUsuario()) return false;
    if (config.getSimulando && config.getSimulando()) return false;
    if (estadoActivo() === false) return false;
  } catch (e) {
    return false;
  }
  return true;
};

const recortar = (texto, maximo) => {
  const limpio = String(texto || '').replace(/\s+/g, ' ').trim();
  return limpio.length > maximo ? limpio.slice(0, maximo) : limpio;
};

// pathname + search sin parámetros con tokens o claves
export const limpiarRuta = (pathname, search = '') => {
  let query = '';
  try {
    const params = new URLSearchParams(search || '');
    const claves = [];
    params.forEach((_, clave) => {
      if (PARAMS_SENSIBLES.test(clave)) claves.push(clave);
    });
    claves.forEach((clave) => params.delete(clave));
    const texto = params.toString();
    query = texto ? `?${texto}` : '';
  } catch (e) {
    query = '';
  }
  return `${pathname || '/'}${query}`.slice(0, 200);
};

const moduloDe = (ruta) => {
  const segmento = String(ruta || '').split('?')[0].split('/').filter(Boolean)[0];
  return segmento || 'inicio';
};

const rutaActual = () => {
  try {
    return limpiarRuta(window.location.pathname, window.location.search);
  } catch (e) {
    return '/';
  }
};

const encolar = (evento) => {
  if (!puedeRegistrar()) return;
  const clave = claveUsuario();
  if (clave !== usuarioDeLaCola) {
    cola = [];
    usuarioDeLaCola = clave;
  }
  const ancho = window.innerWidth || 0;
  cola.push({
    ...evento,
    sesion_id: obtenerSesionId(),
    t: new Date().toISOString(),
    dispositivo: ancho < 768 ? 'movil' : 'escritorio',
    ancho,
  });
  ultimoEvento = ahora();
  if (cola.length > MAX_COLA) cola = cola.slice(cola.length - MAX_COLA);
  if (cola.length >= UMBRAL_ENVIO) enviar();
};

const devolverALaCola = (lote) => {
  cola = lote.concat(cola);
  if (cola.length > MAX_COLA) cola = cola.slice(cola.length - MAX_COLA);
};

const enviar = () => {
  if (enviando || cola.length === 0 || !getAuthToken()) return;
  if (estadoActivo() !== true) return;
  const lote = cola.splice(0, MAX_LOTE);
  enviando = true;
  api.post('/uso/eventos', { eventos: lote })
    .catch((error) => {
      const estado = error && error.response ? error.response.status : 0;
      // Un 4xx (sesión caducada, sin permiso) no se arregla reintentando
      if (estado >= 400 && estado < 500 && estado !== 429) return;
      devolverALaCola(lote);
    })
    .finally(() => {
      enviando = false;
    });
};

// Al ocultar o cerrar la pestaña: fetch con keepalive (sobrevive al cierre)
const enviarAlSalir = () => {
  const token = getAuthToken();
  if (!token || cola.length === 0 || estadoActivo() !== true) return;
  const base = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
  while (cola.length > 0) {
    const lote = cola.splice(0, MAX_LOTE_KEEPALIVE);
    try {
      fetch(`${base}/uso/eventos`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ eventos: lote }),
      }).catch(() => devolverALaCola(lote));
    } catch (e) {
      devolverALaCola(lote);
      return;
    }
  }
};

export const registrarPagina = (pathname, search) => {
  const ruta = limpiarRuta(pathname, search);
  const t = ahora();
  if (paginaActual && paginaActual.ruta === ruta) return;
  const duracion = paginaActual && !paginaActual.oculta ? t - paginaActual.inicio : null;
  encolar({
    tipo: 'pagina',
    ruta,
    modulo: moduloDe(ruta),
    nombre_evento: 'entrada',
    duracion_ms: duracion,
  });
  paginaActual = { ruta, inicio: t, oculta: false };
};

export const registrarAccion = (nombre, detalle = null) => {
  const nombreLimpio = recortar(nombre, 80);
  if (!nombreLimpio) return;
  const ruta = rutaActual();
  encolar({
    tipo: 'accion',
    ruta,
    modulo: moduloDe(ruta),
    nombre_evento: nombreLimpio,
    detalle: detalle ? recortar(detalle, 80) : null,
  });
};

const cerrarPaginaActual = () => {
  if (!paginaActual || paginaActual.oculta) return;
  encolar({
    tipo: 'sesion',
    ruta: paginaActual.ruta,
    modulo: moduloDe(paginaActual.ruta),
    nombre_evento: 'pestana_oculta',
    duracion_ms: ahora() - paginaActual.inicio,
  });
  paginaActual.oculta = true;
};

const alCambiarVisibilidad = () => {
  if (document.visibilityState === 'hidden') {
    cerrarPaginaActual();
    enviarAlSalir();
    return;
  }
  if (!paginaActual) return;
  const caducada = ahora() - ultimoEvento > INACTIVIDAD_MS;
  paginaActual.inicio = ahora();
  paginaActual.oculta = false;
  if (caducada) {
    // Sesión nueva: se vuelve a "entrar" en la pantalla en la que estaba
    encolar({ tipo: 'pagina', ruta: paginaActual.ruta, modulo: moduloDe(paginaActual.ruta), nombre_evento: 'entrada', duracion_ms: null });
  } else {
    encolar({ tipo: 'sesion', ruta: paginaActual.ruta, modulo: moduloDe(paginaActual.ruta), nombre_evento: 'pestana_visible' });
  }
};

const alSalir = () => {
  cerrarPaginaActual();
  enviarAlSalir();
};

// ─── Señales de problema ───────────────────────────────────────────────

const cerrarRafaga = () => {
  if (!rafaga) return;
  const r = rafaga;
  rafaga = null;
  clearTimeout(r.temporizador);
  if (r.n >= MIN_CLICS_RAFAGA) {
    encolar({
      tipo: 'clic_repetido',
      ruta: r.ruta,
      modulo: moduloDe(r.ruta),
      nombre_evento: r.nombre,
      detalle: `${r.n} clics en ${r.ultimo - r.primero} ms`,
    });
  }
};

// Una ráfaga = clics sobre el mismo botón (data-uso o texto) y la misma ruta
// en menos de 2 s desde el primero; se registra una sola vez al cerrarse.
const anotarClic = (nombre, ruta) => {
  const t = ahora();
  const clave = `${nombre}|${ruta}`;
  if (rafaga && rafaga.clave === clave && t - rafaga.primero < VENTANA_RAFAGA_MS) {
    rafaga.n += 1;
    rafaga.ultimo = t;
    return;
  }
  cerrarRafaga();
  rafaga = { clave, nombre, ruta, primero: t, ultimo: t, n: 1, temporizador: setTimeout(cerrarRafaga, VENTANA_RAFAGA_MS) };
};

const registrarError = (mensaje, detalle) => {
  const nombre = recortar(mensaje, 80);
  if (!nombre) return;
  const t = ahora();
  if (ultimosErrores[nombre] && t - ultimosErrores[nombre] < SILENCIO_ERROR_MS) return;
  ultimosErrores[nombre] = t;
  const ruta = rutaActual();
  encolar({ tipo: 'error', ruta, modulo: moduloDe(ruta), nombre_evento: nombre, detalle: detalle ? recortar(detalle, 80) : null });
};

// "/api/presupuestos/5f2c...?x=1" -> "/presupuestos/{id}"
export const rutaApiGenerica = (url) => {
  let camino = String(url || '');
  camino = camino.replace(/^[a-z]+:\/\/[^/]+/i, '').split('?')[0].split('#')[0];
  if (!camino.startsWith('/') && camino.indexOf('/api/') !== -1) {
    camino = camino.slice(camino.indexOf('/api/') + 4); // "undefined/api/..." de API_BASE_URL sin dominio
  }
  camino = camino.replace(/^\/?api(?=\/|$)/, '');
  const partes = camino.split('/').filter(Boolean).map((p) => (
    /^\d+$/.test(p) || RE_UUID.test(p) || (p.length >= 16 && /\d/.test(p)) ? '{id}' : p
  ));
  return `/${partes.join('/')}`;
};

const textoDetalleApi = (datos) => {
  if (!datos) return '';
  const detalle = datos.detail !== undefined ? datos.detail : datos.message;
  if (typeof detalle === 'string') return detalle;
  if (Array.isArray(detalle)) return detalle.map((d) => (d && d.msg) || '').filter(Boolean).join('; ');
  return '';
};

const alErrorApi = (error) => {
  const estado = error && error.response ? error.response.status : 0;
  if (!estado || estado < 400 || estado === 401) return; // 401: sesión caducada
  const cfg = (error && error.config) || {};
  const url = String(cfg.url || '');
  if (url.indexOf('/uso/') !== -1) return;
  const metodo = String(cfg.method || 'get').toUpperCase();
  registrarError(`${metodo} ${rutaApiGenerica(url)}`, `${estado} ${recortar(textoDetalleApi(error.response.data), 80)}`.trim());
};

const alErrorJs = (evento) => {
  const mensaje = evento && evento.message;
  if (!mensaje || /ResizeObserver loop/i.test(mensaje)) return;
  const fichero = evento.filename ? String(evento.filename).split('/').pop() : '';
  registrarError(mensaje, fichero ? `${fichero}:${evento.lineno || 0}` : null);
};

const alPromesaRechazada = (evento) => {
  const motivo = evento && evento.reason;
  if (!motivo || motivo.isAxiosError) return; // las del API ya llegan por el interceptor
  registrarError((motivo && motivo.message) || String(motivo), null);
};

const detectarRecarga = () => {
  try {
    const entradas = window.performance && window.performance.getEntriesByType
      ? window.performance.getEntriesByType('navigation') : [];
    if (!entradas || !entradas[0] || entradas[0].type !== 'reload') return;
    const t = ahora();
    let anterior = 0;
    try {
      anterior = Number(window.sessionStorage.getItem(CLAVE_RECARGA)) || 0;
      window.sessionStorage.setItem(CLAVE_RECARGA, String(t));
    } catch (e) {
      anterior = 0;
    }
    const ruta = rutaActual();
    encolar({
      tipo: 'recarga',
      ruta,
      modulo: moduloDe(ruta),
      detalle: anterior && t - anterior < RECARGA_SEGUIDA_MS ? 'seguida' : null,
    });
  } catch (e) {
    // sin Performance API: no se mide
  }
};

const alHacerClic = (evento) => {
  try {
    const objetivo = evento.target && evento.target.closest ? evento.target : evento.target && evento.target.parentElement;
    if (!objetivo || !objetivo.closest) return;
    // El clic sintético que un <label> manda a su <input> no cuenta dos veces
    if (objetivo.tagName === 'INPUT' || objetivo.tagName === 'TEXTAREA') return;
    const el = objetivo.closest(SELECTOR_ACCION);
    if (!el || el.closest('[data-uso-ignorar]')) return;
    const dataUso = el.getAttribute('data-uso');
    // Solo el texto visible del propio botón o enlace (innerText no incluye valores de inputs)
    const texto = dataUso || recortar(el.innerText || el.getAttribute('aria-label') || el.getAttribute('title') || '', 60);
    if (!texto) return;
    registrarAccion(texto, el.getAttribute('data-uso-detalle') || null);
    if (puedeRegistrar()) anotarClic(recortar(texto, 80), rutaActual());
  } catch (e) {
    // medir el uso nunca rompe un clic
  }
};

// Se llama una vez (RastreadorUso en App.js). Llamadas posteriores solo
// actualizan las funciones de consulta.
export const iniciarUsoErp = ({ getUsuario, getSimulando } = {}) => {
  config = { getUsuario, getSimulando };
  if (iniciado || typeof window === 'undefined') return;
  iniciado = true;
  document.addEventListener('click', alHacerClic, true);
  document.addEventListener('visibilitychange', alCambiarVisibilidad);
  window.addEventListener('pagehide', alSalir);
  window.addEventListener('error', alErrorJs);
  window.addEventListener('unhandledrejection', alPromesaRechazada);
  setObservadorErroresApi(alErrorApi);
  window.setInterval(enviar, INTERVALO_ENVIO_MS);
  detectarRecarga();
};
